import { AlertaProduccionBanner } from '@/components/dominio/AlertaProduccionBanner';
import { AvisoErrorExportacion } from '@/components/dominio/AvisoErrorExportacion';
import { TituloOrdenable } from '@/components/dominio/TituloOrdenable';
import { ViviendaCostosPartida } from '@/components/dominio/ViviendaCostosPartida';
import { ViviendaCuadrillas } from '@/components/dominio/ViviendaCuadrillas';
import { ViviendaPartidas } from '@/components/dominio/ViviendaPartidas';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useViviendaGerencia } from '@/hooks/useAvanceMatriz';
import { useExportacion } from '@/hooks/useExportacion';
import { useFiltrosGlobales } from '@/lib/FiltrosGlobalesContext';
import type { FilaTabla, ReporteExport } from '@/lib/exportarViviendaGerencia';
import { fmtMonto, fmtPct, razon } from '@/lib/formatoGerencia';
import { type OrdenColumna, ordenarHermanos, siguienteOrden } from '@/lib/ordenColumna';
import { useObrasExcluidas } from '@/lib/useObrasExcluidas';
import { CaretDown, CaretRight, CircleNotch, DownloadSimple, FilePdf } from '@phosphor-icons/react';
import { type ReactElement, useMemo, useState } from 'react';

const NOMBRES_MES = [
  'Ene',
  'Feb',
  'Mar',
  'Abr',
  'May',
  'Jun',
  'Jul',
  'Ago',
  'Sep',
  'Oct',
  'Nov',
  'Dic',
];

/** El indirecto (cuenta SSCC) solo se registra desde nov-2025; antes el factor no aplica. */
const INDIRECTO_DESDE_YM = 202511;

type Reporte = 'produccion' | 'mano-obra';

interface Suma {
  prod: number;
  ind: number;
  costo: number;
  mo: number;
}
const cero = (): Suma => ({ prod: 0, ind: 0, costo: 0, mo: 0 });
/** Acumulado por mes (clave "anio-mes") de un nodo del árbol. */
type PorMes = Map<string, Suma>;

function sumar(map: PorMes, key: string, f: Suma) {
  const s = map.get(key) ?? cero();
  s.prod += f.prod;
  s.ind += f.ind;
  s.costo += f.costo;
  s.mo += f.mo;
  map.set(key, s);
}

const keyMes = (anio: number, mes: number) => `${anio}-${mes}`;
const ymDe = (key: string) => {
  const [a, m] = key.split('-').map(Number);
  return (a ?? 0) * 100 + (m ?? 0);
};

/** Indicadores de un conjunto de meses. Las razones solo consideran los meses donde
 *  el numerador tiene dato, para no diluirlas con meses sin registro: el indirecto
 *  existe desde nov-2025 y la mano de obra (cuenta 6010060000000) desde ago-2025. */
function indicadores(porMes: PorMes) {
  let prod = 0;
  let ind = 0;
  let costo = 0;
  let mo = 0;
  let prodConInd = 0;
  let costoConMO = 0;
  for (const [k, s] of porMes) {
    prod += s.prod;
    ind += s.ind;
    costo += s.costo;
    mo += s.mo;
    if (ymDe(k) >= INDIRECTO_DESDE_YM) prodConInd += s.prod;
    if (s.mo !== 0) costoConMO += s.costo;
  }
  return { prod, ind, costo, mo, prodConInd, costoConMO };
}

/** Los 7 valores del reporte para un conjunto de meses, en este orden: Producción,
 *  Indirecto, Factor Ind.÷Prod., Costo, Costo÷Prod., Mano de obra, M.O.÷Costo. */
function valoresMetricas(m: PorMes): (number | null)[] {
  const i = indicadores(m);
  return [
    i.prod || null,
    i.ind || null,
    razon(i.ind, i.prodConInd),
    i.costo || null,
    razon(i.costo, i.prod),
    i.mo || null,
    razon(i.mo, i.costoConMO),
  ];
}

const METRICAS: { titulo: string; tipo: 'monto' | 'pct'; destacada?: boolean }[] = [
  { titulo: 'Producción (venta)', tipo: 'monto' },
  { titulo: 'Indirecto', tipo: 'monto' },
  { titulo: 'Factor Indirecto ÷ Producción', tipo: 'pct', destacada: true },
  { titulo: 'Costo directo', tipo: 'monto' },
  { titulo: 'Costo ÷ Producción', tipo: 'pct', destacada: true },
  { titulo: 'Mano de obra', tipo: 'monto' },
  { titulo: 'Mano de obra ÷ Costo', tipo: 'pct', destacada: true },
];

interface NodoObra {
  worksNo: string;
  display: string | null;
  porMes: PorMes;
}
interface NodoBloque {
  bloque: string;
  key: string;
  porMes: PorMes;
  obras: NodoObra[];
}
interface NodoProyecto {
  proyecto: string;
  porMes: PorMes;
  bloques: NodoBloque[];
}

function IndicadoresVivienda() {
  const [reporte, setReporte] = useState<Reporte>('produccion');
  const [proyectoSel, setProyectoSel] = useState<string>('(todos)');
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  const [orden, setOrden] = useState<OrdenColumna | null>(null);
  const { exportando, error: errorExport, ejecutar, limpiarError } = useExportacion();
  const { filtros } = useFiltrosGlobales();
  const { data, isLoading, error } = useViviendaGerencia();

  const modelo = useMemo(() => {
    const filas = data?.data ?? [];
    const colSet = new Map<string, { key: string; anio: number; mes: number }>();
    const proyectos = new Map<string, NodoProyecto>();
    const bloques = new Map<string, NodoBloque>();
    const obras = new Map<string, NodoObra>();
    const total: PorMes = new Map();

    for (const f of filas) {
      const key = keyMes(f.anio, f.mes);
      if (!colSet.has(key)) colSet.set(key, { key, anio: f.anio, mes: f.mes });
      const suma: Suma = { prod: f.produccion, ind: f.indirecto, costo: f.costo, mo: f.costoMO };

      const proy = f.bloque ?? '(sin proyecto)';
      const blq = f.subBloque ?? '(sin bloque)';
      const bKey = `${proy}|${blq}`;
      let nP = proyectos.get(proy);
      if (!nP) {
        nP = { proyecto: proy, porMes: new Map(), bloques: [] };
        proyectos.set(proy, nP);
      }
      let nB = bloques.get(bKey);
      if (!nB) {
        nB = { bloque: blq, key: bKey, porMes: new Map(), obras: [] };
        bloques.set(bKey, nB);
        nP.bloques.push(nB);
      }
      let nO = obras.get(f.worksNo);
      if (!nO) {
        nO = { worksNo: f.worksNo, display: f.displayName, porMes: new Map() };
        obras.set(f.worksNo, nO);
        nB.obras.push(nO);
      }
      sumar(nO.porMes, key, suma);
      sumar(nB.porMes, key, suma);
      sumar(nP.porMes, key, suma);
      sumar(total, key, suma);
    }

    const cols = [...colSet.values()].sort((a, b) => a.anio - b.anio || a.mes - b.mes);
    const lista = [...proyectos.values()].sort((a, b) => a.proyecto.localeCompare(b.proyecto));
    for (const p of lista) {
      p.bloques.sort((a, b) => a.bloque.localeCompare(b.bloque, 'es', { numeric: true }));
      for (const b of p.bloques) b.obras.sort((x, y) => x.worksNo.localeCompare(y.worksNo));
    }
    return { cols, proyectos: lista, total };
  }, [data]);

  const toggle = (key: string) =>
    setExpandidos((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <CircleNotch className="h-4 w-4 animate-spin" weight="bold" /> Cargando…
      </div>
    );
  }
  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="pt-6">
          <p className="text-destructive">Error al cargar: {error.message}</p>
        </CardContent>
      </Card>
    );
  }

  const { cols } = modelo;
  const escopa: PorMes =
    proyectoSel === '(todos)'
      ? modelo.total
      : (modelo.proyectos.find((p) => p.proyecto === proyectoSel)?.porMes ?? new Map());
  const esProd = reporte === 'produccion';
  const kpi = indicadores(escopa);
  const periodo =
    cols.length > 0
      ? `${NOMBRES_MES[(cols[0]?.mes ?? 1) - 1]} ${cols[0]?.anio} – ${NOMBRES_MES[(cols[cols.length - 1]?.mes ?? 1) - 1]} ${cols[cols.length - 1]?.anio}`
      : 'sin datos';
  const iniciaAnio = (idx: number) => idx === 0 || cols[idx - 1]?.anio !== cols[idx]?.anio;
  const anios = [...new Set(cols.map((c) => c.anio))];

  // ---- Filas de la tabla de evolución mensual (según el reporte) ----
  interface FilaMetrica {
    etiqueta: string;
    tipo: 'monto' | 'pct';
    destacada?: boolean;
    /** Valor para un conjunto de meses (una columna = 1 mes; Total = todos). */
    valor: (ms: PorMes) => string;
  }
  const filasMetricas: FilaMetrica[] = esProd
    ? [
        {
          etiqueta: 'Producción (venta)',
          tipo: 'monto',
          valor: (m) => fmtMonto(indicadores(m).prod),
        },
        { etiqueta: 'Indirecto', tipo: 'monto', valor: (m) => fmtMonto(indicadores(m).ind) },
        {
          etiqueta: 'Factor Indirecto ÷ Producción',
          tipo: 'pct',
          destacada: true,
          valor: (m) => {
            const i = indicadores(m);
            return fmtPct(i.ind, i.prodConInd);
          },
        },
        { etiqueta: 'Costo directo', tipo: 'monto', valor: (m) => fmtMonto(indicadores(m).costo) },
        {
          etiqueta: 'Costo ÷ Producción',
          tipo: 'pct',
          destacada: true,
          valor: (m) => {
            const i = indicadores(m);
            return fmtPct(i.costo, i.prod);
          },
        },
      ]
    : [
        { etiqueta: 'Costo directo', tipo: 'monto', valor: (m) => fmtMonto(indicadores(m).costo) },
        { etiqueta: 'Mano de obra', tipo: 'monto', valor: (m) => fmtMonto(indicadores(m).mo) },
        {
          etiqueta: 'Mano de obra ÷ Costo',
          tipo: 'pct',
          destacada: true,
          valor: (m) => {
            const i = indicadores(m);
            return fmtPct(i.mo, i.costoConMO);
          },
        },
      ];

  const soloMes = (key: string): PorMes => {
    const s = escopa.get(key);
    return s ? new Map([[key, s]]) : new Map();
  };

  // ---- Tabla de detalle por Proyecto → Bloque → Obra (totales del período) ----
  const columnasDetalle: {
    titulo: string;
    /** Posición de la métrica en `valoresMetricas` (clave para ordenar). */
    idx: number;
    valor: (m: PorMes) => string;
    pct?: boolean;
  }[] = esProd
    ? [
        { titulo: 'Producción', idx: 0, valor: (m) => fmtMonto(indicadores(m).prod) },
        { titulo: 'Indirecto', idx: 1, valor: (m) => fmtMonto(indicadores(m).ind) },
        {
          titulo: 'Factor Ind. ÷ Prod.',
          idx: 2,
          pct: true,
          valor: (m) => {
            const i = indicadores(m);
            return fmtPct(i.ind, i.prodConInd);
          },
        },
        { titulo: 'Costo directo', idx: 3, valor: (m) => fmtMonto(indicadores(m).costo) },
        {
          titulo: 'Costo ÷ Prod.',
          idx: 4,
          pct: true,
          valor: (m) => {
            const i = indicadores(m);
            return fmtPct(i.costo, i.prod);
          },
        },
      ]
    : [
        { titulo: 'Costo directo', idx: 3, valor: (m) => fmtMonto(indicadores(m).costo) },
        { titulo: 'Mano de obra', idx: 5, valor: (m) => fmtMonto(indicadores(m).mo) },
        {
          titulo: 'M.O. ÷ Costo',
          idx: 6,
          pct: true,
          valor: (m) => {
            const i = indicadores(m);
            return fmtPct(i.mo, i.costoConMO);
          },
        },
      ];

  const celdasDetalle = (porMes: PorMes) =>
    columnasDetalle.map((c) => (
      <TableCell
        key={c.titulo}
        className={`text-right text-xs tabular-nums ${c.pct ? 'bg-emerald-50/60 font-semibold' : ''}`}
      >
        {c.valor(porMes)}
      </TableCell>
    ));

  /** Hermanos de un nivel en el orden elegido (o el natural si no hay orden). */
  const hermanos = <T extends { porMes: PorMes }>(items: T[]) =>
    ordenarHermanos(items, orden, (n) => valoresMetricas(n.porMes));
  const alOrdenar = (idx: number) => setOrden((actual) => siguienteOrden(actual, idx));

  const filasDetalle: ReactElement[] = [];
  for (const p of hermanos(modelo.proyectos)) {
    const pKey = `P|${p.proyecto}`;
    const pOpen = expandidos.has(pKey);
    filasDetalle.push(
      <TableRow key={pKey} className="bg-emerald-100 font-semibold">
        <TableCell className="sticky left-0 z-10 bg-emerald-100 text-xs">
          <button
            type="button"
            onClick={() => toggle(pKey)}
            className="flex items-center gap-1 hover:underline"
          >
            {pOpen ? (
              <CaretDown className="h-3 w-3" weight="bold" />
            ) : (
              <CaretRight className="h-3 w-3" weight="bold" />
            )}
            {p.proyecto}
          </button>
        </TableCell>
        {celdasDetalle(p.porMes)}
      </TableRow>,
    );
    if (!pOpen) continue;
    for (const b of hermanos(p.bloques)) {
      const bKey = `B|${b.key}`;
      const bOpen = expandidos.has(bKey);
      filasDetalle.push(
        <TableRow key={bKey} className="bg-emerald-50 font-medium">
          <TableCell className="sticky left-0 z-10 bg-emerald-50 text-xs">
            <button
              type="button"
              onClick={() => toggle(bKey)}
              className="flex items-center gap-1 pl-4 hover:underline"
            >
              {bOpen ? (
                <CaretDown className="h-3 w-3" weight="bold" />
              ) : (
                <CaretRight className="h-3 w-3" weight="bold" />
              )}
              {b.bloque}
            </button>
          </TableCell>
          {celdasDetalle(b.porMes)}
        </TableRow>,
      );
      if (!bOpen) continue;
      for (const o of hermanos(b.obras)) {
        filasDetalle.push(
          <TableRow key={`O|${o.worksNo}`}>
            <TableCell className="sticky left-0 z-10 bg-background text-xs">
              <div className="pl-9">
                <div className="font-medium">{o.worksNo}</div>
                {o.display && o.display !== o.worksNo && (
                  <div className="max-w-[200px] truncate text-[10px] text-muted-foreground">
                    {o.display}
                  </div>
                )}
              </div>
            </TableCell>
            {celdasDetalle(o.porMes)}
          </TableRow>,
        );
      }
    }
  }

  const todasKeys = new Set<string>();
  for (const p of modelo.proyectos) {
    todasKeys.add(`P|${p.proyecto}`);
    for (const b of p.bloques) todasKeys.add(`B|${b.key}`);
  }

  const tarjetas = esProd
    ? [
        { titulo: 'Producción (venta)', valor: fmtMonto(kpi.prod), sub: periodo },
        { titulo: 'Indirecto', valor: fmtMonto(kpi.ind), sub: 'Cuenta SSCC 70-10-000-000-100' },
        {
          titulo: 'Factor Indirecto ÷ Producción',
          valor: fmtPct(kpi.ind, kpi.prodConInd),
          sub: 'Solo meses con indirecto (desde nov-2025)',
          destacada: true,
        },
        { titulo: 'Costo directo', valor: fmtMonto(kpi.costo), sub: 'Sin SSCC ni partidas CI' },
        {
          titulo: 'Costo ÷ Producción',
          valor: fmtPct(kpi.costo, kpi.prod),
          sub: 'Costo directo sobre venta',
          destacada: true,
        },
      ]
    : [
        { titulo: 'Costo directo', valor: fmtMonto(kpi.costo), sub: periodo },
        { titulo: 'Mano de obra', valor: fmtMonto(kpi.mo), sub: 'Cuenta 6010060000000' },
        {
          titulo: 'Mano de obra ÷ Costo',
          valor: fmtPct(kpi.mo, kpi.costoConMO),
          sub: 'Solo meses con M.O. registrada',
          destacada: true,
        },
      ];

  /** Modelo neutro para Excel/PDF: incluye los dos reportes completos (el alcance
   *  del selector aplica a la evolución mensual; el detalle siempre es de todo). */
  const armarReporte = (): ReporteExport => {
    const alcance = proyectoSel === '(todos)' ? 'Todas las viviendas' : `Proyecto ${proyectoSel}`;
    const filtrosActivos = [
      filtros.filtroProyecto && `Proyecto: ${filtros.filtroProyecto}`,
      filtros.filtroBloque && `Bloque: ${filtros.filtroBloque}`,
      filtros.filtroObra && `Obra: ${filtros.filtroObra}`,
      filtros.filtroModelo && `Modelo: ${filtros.filtroModelo}`,
      filtros.estadosSel?.length && `Estado de venta: ${filtros.estadosSel.join(', ')}`,
    ].filter(Boolean);
    const hoy = new Date().toLocaleDateString('es-CR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    const k = indicadores(escopa);
    const totalEscopa = valoresMetricas(escopa);

    const evolucion = {
      titulo: 'Evolución mensual',
      encabezado: alcance,
      columnas: [
        ...cols.map((c) => ({
          titulo: `${NOMBRES_MES[c.mes - 1]} ${String(c.anio).slice(2)}`,
          grupo: String(c.anio),
        })),
        { titulo: 'Total período', grupo: 'Total' },
      ],
      filas: METRICAS.map(
        (m, j): FilaTabla => ({
          nivel: 0,
          etiqueta: m.titulo,
          tipo: m.tipo,
          destacada: m.destacada,
          valores: [
            ...cols.map((c) => valoresMetricas(soloMes(c.key))[j] ?? null),
            totalEscopa[j] ?? null,
          ],
        }),
      ),
    };

    const filasDet: FilaTabla[] = [];
    for (const p of hermanos(modelo.proyectos)) {
      filasDet.push({ nivel: 0, etiqueta: p.proyecto, valores: valoresMetricas(p.porMes) });
      for (const b of hermanos(p.bloques)) {
        filasDet.push({ nivel: 1, etiqueta: b.bloque, valores: valoresMetricas(b.porMes) });
        for (const o of hermanos(b.obras)) {
          filasDet.push({
            nivel: 2,
            etiqueta: o.worksNo,
            sublabel: o.display && o.display !== o.worksNo ? o.display : null,
            valores: valoresMetricas(o.porMes),
          });
        }
      }
    }
    filasDet.push({
      nivel: 0,
      total: true,
      etiqueta: 'Total viviendas',
      valores: valoresMetricas(modelo.total),
    });

    return {
      titulo: 'Vivienda Gerencia',
      subtitulo: [
        `Viviendas VB, VC y VN (área PRO VIVIENDA) · Alcance de la evolución: ${alcance}`,
        `Período: ${periodo} · Generado el ${hoy}${filtrosActivos.length ? ` · ${filtrosActivos.join(' · ')}` : ''}`,
        'Factor de indirecto: solo meses con indirecto (desde nov-2025). M.O. ÷ Costo: solo meses con M.O. registrada.',
      ],
      kpis: [
        { titulo: 'Producción (venta)', valor: fmtMonto(k.prod) },
        { titulo: 'Indirecto', valor: fmtMonto(k.ind) },
        { titulo: 'Factor Ind. ÷ Prod.', valor: fmtPct(k.ind, k.prodConInd) },
        { titulo: 'Costo directo', valor: fmtMonto(k.costo) },
        { titulo: 'Costo ÷ Prod.', valor: fmtPct(k.costo, k.prod) },
        { titulo: 'Mano de obra', valor: fmtMonto(k.mo) },
        { titulo: 'M.O. ÷ Costo', valor: fmtPct(k.mo, k.costoConMO) },
      ],
      evolucion,
      detalle: {
        titulo: 'Detalle',
        arbol: true,
        encabezado: 'Proyecto / Bloque / Obra',
        columnas: METRICAS.map((m) => ({ titulo: m.titulo, tipo: m.tipo })),
        filas: filasDet,
      },
      nombreArchivo: `vivienda-gerencia-${new Date().toISOString().slice(0, 10)}`,
    };
  };

  const exportar = (formato: 'xlsx' | 'pdf') =>
    ejecutar(formato, async () => {
      const rep = armarReporte();
      const lib = await import('@/lib/exportarViviendaGerencia');
      if (formato === 'xlsx') await lib.exportarViviendaGerenciaExcel(rep);
      else await lib.exportarViviendaGerenciaPdf(rep);
    });

  const chip = (activo: boolean) =>
    `px-2.5 py-1 ${activo ? 'bg-emerald-700 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-100'}`;

  return (
    <div className="space-y-4">
      {errorExport && <AvisoErrorExportacion error={errorExport} onCerrar={limpiarError} />}
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <div className="inline-flex overflow-hidden rounded-md border border-zinc-300">
          <button
            type="button"
            onClick={() => {
              setReporte('produccion');
              setOrden(null);
            }}
            className={chip(esProd)}
          >
            Producción · Indirecto · Costo
          </button>
          <button
            type="button"
            onClick={() => {
              setReporte('mano-obra');
              setOrden(null);
            }}
            className={chip(!esProd)}
          >
            Costo vs Mano de obra
          </button>
        </div>
        <div className="inline-flex overflow-hidden rounded-md border border-zinc-300">
          {['(todos)', ...modelo.proyectos.map((p) => p.proyecto)].map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setProyectoSel(p)}
              className={chip(proyectoSel === p)}
            >
              {p === '(todos)' ? 'Todas las viviendas' : p}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => exportar('xlsx')}
            disabled={exportando !== null}
            className="inline-flex items-center gap-1 rounded-md border border-emerald-700 bg-white px-2 py-1 font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
          >
            <DownloadSimple className="h-3.5 w-3.5" weight="bold" />
            {exportando === 'xlsx' ? 'Generando…' : 'Exportar a Excel'}
          </button>
          <button
            type="button"
            onClick={() => exportar('pdf')}
            disabled={exportando !== null}
            className="inline-flex items-center gap-1 rounded-md border border-emerald-700 bg-white px-2 py-1 font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
          >
            <FilePdf className="h-3.5 w-3.5" weight="bold" />
            {exportando === 'pdf' ? 'Generando…' : 'Exportar a PDF'}
          </button>
        </div>
      </div>

      <div className={`grid gap-3 ${esProd ? 'sm:grid-cols-2 lg:grid-cols-5' : 'sm:grid-cols-3'}`}>
        {tarjetas.map((t) => (
          <Card key={t.titulo} className={t.destacada ? 'border-emerald-300 bg-emerald-50/60' : ''}>
            <CardContent className="p-4">
              <div className="text-xs font-medium text-muted-foreground">{t.titulo}</div>
              <div className="mt-1 text-xl font-semibold tabular-nums">{t.valor}</div>
              <div className="mt-0.5 text-[11px] text-muted-foreground">{t.sub}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="border-b px-4 py-2 text-sm font-semibold">
            Evolución mensual ·{' '}
            {proyectoSel === '(todos)' ? 'Todas las viviendas' : `Proyecto ${proyectoSel}`}
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="sticky left-0 z-30 min-w-[220px] bg-background" />
                {anios.map((anio) => (
                  <TableHead
                    key={anio}
                    colSpan={cols.filter((c) => c.anio === anio).length}
                    className="border-l border-zinc-200 text-center text-xs"
                  >
                    {anio}
                  </TableHead>
                ))}
                <TableHead className="border-l border-zinc-300 text-center text-xs">
                  Total
                </TableHead>
              </TableRow>
              <TableRow>
                <TableHead className="sticky left-0 z-30 bg-background" />
                {cols.map((c, idx) => (
                  <TableHead
                    key={c.key}
                    className={`min-w-[88px] text-right text-xs ${iniciaAnio(idx) ? 'border-l border-zinc-200' : ''}`}
                  >
                    {NOMBRES_MES[c.mes - 1]}
                  </TableHead>
                ))}
                <TableHead className="min-w-[100px] border-l border-zinc-300 text-right text-xs">
                  Período
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filasMetricas.map((fm) => (
                <TableRow
                  key={fm.etiqueta}
                  className={fm.destacada ? 'bg-emerald-50 font-semibold' : ''}
                >
                  <TableCell
                    className={`sticky left-0 z-10 text-xs ${fm.destacada ? 'bg-emerald-50' : 'bg-background'}`}
                  >
                    {fm.etiqueta}
                  </TableCell>
                  {cols.map((c, idx) => (
                    <TableCell
                      key={c.key}
                      className={`text-right text-xs tabular-nums ${iniciaAnio(idx) ? 'border-l border-zinc-200' : ''}`}
                    >
                      {fm.valor(soloMes(c.key))}
                    </TableCell>
                  ))}
                  <TableCell className="border-l border-zinc-300 text-right text-xs font-semibold tabular-nums">
                    {fm.valor(escopa)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="flex flex-wrap items-center gap-3 border-b px-4 py-2 text-sm">
            <span className="font-semibold">Detalle por Proyecto → Bloque → Obra</span>
            <span className="text-xs text-muted-foreground">
              Acumulado del período: {periodo} · click en un encabezado para ordenar
            </span>
            <span className="ml-auto flex items-center gap-2 text-xs">
              <button
                type="button"
                onClick={() => setExpandidos(new Set(todasKeys))}
                className="text-blue-600 hover:underline"
              >
                Expandir todo
              </button>
              <span className="text-zinc-300">|</span>
              <button
                type="button"
                onClick={() => setExpandidos(new Set())}
                className="text-blue-600 hover:underline"
              >
                Colapsar
              </button>
            </span>
          </div>
          <Table wrapperClassName="max-h-[calc(100vh-260px)]">
            <TableHeader>
              <TableRow>
                <TableHead className="sticky left-0 top-0 z-30 min-w-[220px] bg-background">
                  Proyecto / Bloque / Obra
                </TableHead>
                {columnasDetalle.map((c) => (
                  <TableHead
                    key={c.titulo}
                    className="sticky top-0 z-20 min-w-[120px] bg-background text-right text-xs"
                  >
                    <TituloOrdenable
                      titulo={c.titulo}
                      idx={c.idx}
                      orden={orden}
                      onOrdenar={alOrdenar}
                    />
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filasDetalle}
              <TableRow className="border-t-2 border-zinc-300 bg-zinc-100 font-bold">
                <TableCell className="sticky left-0 z-10 bg-zinc-100 text-xs">
                  Total viviendas
                </TableCell>
                {celdasDetalle(modelo.total)}
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <p className="text-[11px] text-muted-foreground">
        Producción: movimientos BC tipo Venta (cuenta 40-10-000-000-001). Indirecto: gasto a
        Servicios Centrales (70-10-000-000-100), disponible desde nov-2025. Costo directo: costos de
        obra sin la cuenta SSCC ni partidas indirectas (CI). Mano de obra: cuenta 6010060000000.
      </p>
    </div>
  );
}

type Vista = 'indicadores' | 'partidas' | 'por-partida' | 'cuadrillas';

/** Sección Vivienda Gerencia: dos informes sobre las mismas viviendas (VB/VC/VN). */
export function ViviendaGerenciaPantalla() {
  const [vista, setVista] = useState<Vista>('indicadores');
  // Obras excluidas: compartidas por los dos informes por partida.
  const [excluidas, setExcluidas] = useObrasExcluidas();
  const tab = (activa: boolean) =>
    `-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
      activa
        ? 'border-emerald-700 text-emerald-800'
        : 'border-transparent text-zinc-500 hover:text-zinc-800'
    }`;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Vivienda Gerencia</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Informes gerenciales de las viviendas (proyectos VB, VC y VN · área PRO VIVIENDA).
          Respetan los filtros globales de Proyecto, Bloque, Obra, Modelo, Períodos y Estado de
          venta.
        </p>
      </div>

      <AlertaProduccionBanner />

      <div className="flex gap-1 border-b border-zinc-200">
        <button
          type="button"
          onClick={() => setVista('indicadores')}
          className={tab(vista === 'indicadores')}
        >
          Indicadores
        </button>
        <button
          type="button"
          onClick={() => setVista('partidas')}
          className={tab(vista === 'partidas')}
        >
          Costo por obra y partida
        </button>
        <button
          type="button"
          onClick={() => setVista('por-partida')}
          className={tab(vista === 'por-partida')}
        >
          Costo por partida
        </button>
        <button
          type="button"
          onClick={() => setVista('cuadrillas')}
          className={tab(vista === 'cuadrillas')}
        >
          Costo por cuadrilla
        </button>
      </div>

      {vista === 'indicadores' && <IndicadoresVivienda />}
      {vista === 'partidas' && (
        <ViviendaCostosPartida excluidas={excluidas} onExcluidas={setExcluidas} />
      )}
      {vista === 'cuadrillas' && (
        <ViviendaCuadrillas excluidas={excluidas} onExcluidas={setExcluidas} />
      )}
      {vista === 'por-partida' && (
        <ViviendaPartidas excluidas={excluidas} onExcluidas={setExcluidas} />
      )}
    </div>
  );
}
